import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import StoreMultiSelectFilter from "../components/StoreMultiSelectFilter";
import {
  addStockItemsToBatch,
  createStockBatch,
  getStockBatches,
  getStockItems,
  getStoreFactories,
  updateStockItem,
} from "../production/service";
import { toErrorText } from "./storeSalesDashboardHelpers";

const ITEM_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "planning", label: "Planning" },
];

const EMPTY_FILTER_LABEL = "—";

const getMLabelFilterValue = (row) =>
  String(row.m_label ?? "").trim() || EMPTY_FILTER_LABEL;

const getDescriptionFilterValue = (row) =>
  String(row.description ?? "").trim() || EMPTY_FILTER_LABEL;

const getThicknessFilterValue = (row) => String(row.thickness ?? "");

const getRemarkFilterValue = (row) =>
  String(row.remark ?? "").trim() || EMPTY_FILTER_LABEL;

const uniqueSortedOptions = (values) => {
  const set = new Set();

  values.forEach((value) => {
    const normalized = String(value ?? "").trim();
    set.add(normalized || EMPTY_FILTER_LABEL);
  });

  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
};

const hasActiveColumnFilters = ({
  mLabelFilter,
  descriptionFilter,
  thicknessFilter,
  remarkFilter,
}) =>
  mLabelFilter.length > 0 ||
  descriptionFilter.length > 0 ||
  thicknessFilter.length > 0 ||
  remarkFilter.length > 0;

const StoreStockItemsSection = () => {
  const [statusFilter, setStatusFilter] = useState("draft");
  const [stockItems, setStockItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [mLabelFilter, setMLabelFilter] = useState([]);
  const [descriptionFilter, setDescriptionFilter] = useState([]);
  const [thicknessFilter, setThicknessFilter] = useState([]);
  const [remarkFilter, setRemarkFilter] = useState([]);
  const [isFactoryModalOpen, setIsFactoryModalOpen] = useState(false);
  const [isExistingBatchModalOpen, setIsExistingBatchModalOpen] =
    useState(false);
  const [factories, setFactories] = useState([]);
  const [draftBatches, setDraftBatches] = useState([]);
  const [factoryId, setFactoryId] = useState("");
  const [existingBatchId, setExistingBatchId] = useState("");
  const [factoryLoading, setFactoryLoading] = useState(false);
  const [existingBatchLoading, setExistingBatchLoading] = useState(false);
  const [factoryError, setFactoryError] = useState(null);
  const [existingBatchError, setExistingBatchError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStockItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getStockItems();
      setStockItems(rows);
    } catch (err) {
      setStockItems([]);
      setError(toErrorText(err, "Unable to load stock items."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStockItems();
  }, [loadStockItems]);

  useEffect(() => {
    setSelectedIds([]);
    setFactoryError(null);
    setMLabelFilter([]);
    setDescriptionFilter([]);
    setThicknessFilter([]);
    setRemarkFilter([]);
  }, [statusFilter]);

  const statusFilteredItems = useMemo(
    () => stockItems.filter((row) => row.status === statusFilter),
    [stockItems, statusFilter]
  );

  const mLabelFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredItems.map((row) => getMLabelFilterValue(row))
      ),
    [statusFilteredItems]
  );

  const descriptionFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredItems.map((row) => getDescriptionFilterValue(row))
      ),
    [statusFilteredItems]
  );

  const thicknessFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredItems.map((row) => getThicknessFilterValue(row))
      ),
    [statusFilteredItems]
  );

  const remarkFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredItems.map((row) => getRemarkFilterValue(row))
      ),
    [statusFilteredItems]
  );

  const displayedItems = useMemo(() => {
    return statusFilteredItems.filter((row) => {
      if (
        mLabelFilter.length > 0 &&
        !mLabelFilter.includes(getMLabelFilterValue(row))
      ) {
        return false;
      }

      if (
        descriptionFilter.length > 0 &&
        !descriptionFilter.includes(getDescriptionFilterValue(row))
      ) {
        return false;
      }

      if (
        thicknessFilter.length > 0 &&
        !thicknessFilter.includes(getThicknessFilterValue(row))
      ) {
        return false;
      }

      if (
        remarkFilter.length > 0 &&
        !remarkFilter.includes(getRemarkFilterValue(row))
      ) {
        return false;
      }

      return true;
    });
  }, [
    statusFilteredItems,
    mLabelFilter,
    descriptionFilter,
    thicknessFilter,
    remarkFilter,
  ]);

  useEffect(() => {
    const visibleIds = new Set(
      displayedItems.map((row) => row.id).filter((id) => id != null)
    );

    setSelectedIds((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [displayedItems]);

  const columnFiltersActive = hasActiveColumnFilters({
    mLabelFilter,
    descriptionFilter,
    thicknessFilter,
    remarkFilter,
  });

  const clearColumnFilters = () => {
    setMLabelFilter([]);
    setDescriptionFilter([]);
    setThicknessFilter([]);
    setRemarkFilter([]);
  };

  const showSelectionColumn =
    statusFilter === "draft" || statusFilter === "planning";

  const pillClassName = useMemo(
    () => (value) =>
      `btn btn-sm rounded-pill ${
        statusFilter === value ? "btn-primary" : "btn-light border text-dark"
      }`,
    [statusFilter]
  );

  const visibleIds = useMemo(
    () => displayedItems.map((row) => row.id).filter((id) => id != null),
    [displayedItems]
  );

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const selectedSqftTotal = useMemo(() => {
    if (selectedIds.length === 0) return null;

    return displayedItems
      .filter((row) => selectedIds.includes(row.id))
      .reduce((sum, row) => sum + Number(row.sqft), 0);
  }, [displayedItems, selectedIds]);

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const toggleAllVisible = () => {
    setSelectedIds(allVisibleSelected ? [] : visibleIds);
  };

  const runAction = async (action) => {
    setActionLoading(true);
    setError(null);
    try {
      await action();
      setSelectedIds([]);
      await loadStockItems();
    } catch (err) {
      setError(toErrorText(err, "Unable to update stock items."));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmAndRun = (message, action) => {
    if (!window.confirm(message)) return;
    runAction(action);
  };

  const handleMoveToPlanning = () =>
    confirmAndRun(`Move ${selectedIds.length} stock item(s) to Planning?`, () =>
      Promise.all(selectedIds.map((id) => updateStockItem(id, "planning")))
    );

  const handleMoveToDraft = () =>
    confirmAndRun(
      `Move ${selectedIds.length} stock item(s) back to Draft?`,
      () => Promise.all(selectedIds.map((id) => updateStockItem(id, "draft")))
    );

  const loadFactories = useCallback(async () => {
    setFactoryLoading(true);
    setFactoryError(null);
    try {
      const rows = await getStoreFactories();
      setFactories(rows);
    } catch (err) {
      setFactories([]);
      setFactoryError(toErrorText(err, "Unable to load factories."));
    } finally {
      setFactoryLoading(false);
    }
  }, []);

  const loadDraftBatches = useCallback(async () => {
    setExistingBatchLoading(true);
    setExistingBatchError(null);
    try {
      const rows = await getStockBatches({ status: "draft" });
      setDraftBatches(rows);
    } catch (err) {
      setDraftBatches([]);
      setExistingBatchError(toErrorText(err, "Unable to load batches."));
    } finally {
      setExistingBatchLoading(false);
    }
  }, []);

  const openFactoryModal = () => {
    setFactoryId("");
    setFactoryError(null);
    setIsFactoryModalOpen(true);
    loadFactories();
  };

  const closeFactoryModal = () => {
    if (actionLoading) return;
    setIsFactoryModalOpen(false);
    setFactoryId("");
    setFactoryError(null);
  };

  const openExistingBatchModal = () => {
    setExistingBatchId("");
    setExistingBatchError(null);
    setIsExistingBatchModalOpen(true);
    loadDraftBatches();
  };

  const closeExistingBatchModal = () => {
    if (actionLoading) return;
    setIsExistingBatchModalOpen(false);
    setExistingBatchId("");
    setExistingBatchError(null);
  };

  const handleCreateBatch = () => {
    runAction(async () => {
      await createStockBatch({
        factory_id: Number(factoryId),
        stock_item_ids: selectedIds,
      });
      closeFactoryModal();
    });
  };

  const handleAddToExistingBatch = () => {
    runAction(async () => {
      await addStockItemsToBatch(Number(existingBatchId), {
        stock_item_ids: selectedIds,
      });
      closeExistingBatchModal();
    });
  };

  const renderActions = () => {
    if (statusFilter === "draft") {
      return (
        <Button
          color="primary"
          size="sm"
          disabled={selectedIds.length === 0 || actionLoading}
          onClick={handleMoveToPlanning}
        >
          Move to Planning
        </Button>
      );
    }

    if (statusFilter === "planning") {
      return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100">
          <div className="d-flex flex-wrap gap-2">
            <Button
              color="secondary"
              size="sm"
              disabled={selectedIds.length === 0 || actionLoading}
              onClick={handleMoveToDraft}
            >
              Move back to Draft
            </Button>
            <Button
              color="primary"
              size="sm"
              disabled={selectedIds.length === 0 || actionLoading}
              onClick={openFactoryModal}
            >
              Create new Batch
            </Button>
            <Button
              color="primary"
              outline
              size="sm"
              disabled={selectedIds.length === 0 || actionLoading}
              onClick={openExistingBatchModal}
            >
              Add to Existing Batch
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  const actions = renderActions();
  const columnCount = showSelectionColumn ? 7 : 6;
  const emptyMessage =
    statusFilteredItems.length === 0
      ? "No stock items found."
      : "No stock items match filters.";

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="text-muted small me-2">Status:</span>
          {ITEM_STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={pillClassName(option.value)}
              onClick={() => setStatusFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {selectedSqftTotal != null && (
          <span className="text-muted small d-flex flex-wrap gap-3">
            <span>Total sqft: {selectedSqftTotal.toFixed(2)}</span>
            <span>
              Approx. Boards: {(selectedSqftTotal / 0.95 / 32).toFixed(2)}
            </span>
          </span>
        )}
      </div>

      {actions && <div className="mb-3">{actions}</div>}

      {!loading && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <StoreMultiSelectFilter
            label="M Label"
            options={mLabelFilterOptions}
            value={mLabelFilter}
            onChange={setMLabelFilter}
            minWidth="120px"
          />
          <StoreMultiSelectFilter
            label="Description"
            options={descriptionFilterOptions}
            value={descriptionFilter}
            onChange={setDescriptionFilter}
            minWidth="180px"
          />
          <StoreMultiSelectFilter
            label="Thickness"
            options={thicknessFilterOptions}
            value={thicknessFilter}
            onChange={setThicknessFilter}
            minWidth="140px"
          />
          <StoreMultiSelectFilter
            label="Remark"
            options={remarkFilterOptions}
            value={remarkFilter}
            onChange={setRemarkFilter}
            minWidth="160px"
          />
          {columnFiltersActive && (
            <Button
              color="light"
              size="sm"
              className="border text-dark"
              onClick={clearColumnFilters}
            >
              Clear filters
            </Button>
          )}
        </div>
      )}

      {error && (
        <Alert color="danger" fade={false}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="py-5 text-center text-muted">
          <Spinner className="me-2" />
          Loading…
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover responsive className="mb-0 store-sales-table">
            <thead className="store-sales-table__head">
              <tr>
                {showSelectionColumn && (
                  <th style={{ width: "3rem" }}>
                    <Input
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={visibleIds.length === 0 || actionLoading}
                      onChange={toggleAllVisible}
                      aria-label="Select all visible stock items"
                    />
                  </th>
                )}
                <th>M Label</th>
                <th>Description</th>
                <th>Thickness</th>
                <th>FB Length</th>
                <th>FB Width</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {displayedItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="text-muted text-center py-4"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                displayedItems.map((row) => (
                  <tr key={row.id}>
                    {showSelectionColumn && (
                      <td>
                        <Input
                          type="checkbox"
                          checked={selectedIds.includes(row.id)}
                          disabled={actionLoading}
                          onChange={() => toggleRow(row.id)}
                          aria-label={`Select stock item ${row.id}`}
                        />
                      </td>
                    )}
                    <td>{row.m_label}</td>
                    <td>{row.description}</td>
                    <td>{row.thickness}</td>
                    <td>{row.fb_length}</td>
                    <td>{row.fb_width}</td>
                    <td>{row.remark || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
      <Modal isOpen={isFactoryModalOpen} toggle={closeFactoryModal} centered>
        <ModalHeader toggle={closeFactoryModal}>Create new Batch</ModalHeader>
        <ModalBody>
          {factoryError && (
            <Alert color="danger" fade={false}>
              {factoryError}
            </Alert>
          )}
          <label htmlFor="stock-factory-select" className="form-label mb-2">
            Factory
          </label>
          <Input
            id="stock-factory-select"
            type="select"
            value={factoryId}
            disabled={factoryLoading || actionLoading}
            onChange={(event) => setFactoryId(event.target.value)}
          >
            <option value="">Select a factory</option>
            {factories.map((factory) => (
              <option key={factory.id} value={factory.id}>
                {factory.name}
              </option>
            ))}
          </Input>
          {factoryLoading && (
            <div className="mt-3 text-muted small">
              <Spinner size="sm" className="me-2" />
              Loading factories...
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={closeFactoryModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleCreateBatch}
            disabled={!factoryId || actionLoading || factoryLoading}
          >
            Submit
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={isExistingBatchModalOpen}
        toggle={closeExistingBatchModal}
        centered
      >
        <ModalHeader toggle={closeExistingBatchModal}>
          Add to Existing Batch
        </ModalHeader>
        <ModalBody>
          {existingBatchError && (
            <Alert color="danger" fade={false}>
              {existingBatchError}
            </Alert>
          )}
          {existingBatchLoading ? (
            <div className="text-muted small">
              <Spinner size="sm" className="me-2" />
              Loading batches...
            </div>
          ) : draftBatches.length === 0 ? (
            <Alert color="warning" fade={false} className="mb-0">
              No draft batches available.
            </Alert>
          ) : (
            <>
              <label
                htmlFor="stock-existing-batch-select"
                className="form-label mb-2"
              >
                Batch
              </label>
              <Input
                id="stock-existing-batch-select"
                type="select"
                value={existingBatchId}
                disabled={actionLoading}
                onChange={(event) => setExistingBatchId(event.target.value)}
              >
                <option value="">Select a batch</option>
                {draftBatches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name} — {batch.factory.name}
                  </option>
                ))}
              </Input>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={closeExistingBatchModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleAddToExistingBatch}
            disabled={
              !existingBatchId ||
              actionLoading ||
              existingBatchLoading ||
              draftBatches.length === 0
            }
          >
            Submit
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StoreStockItemsSection;
